import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import '../styles/GameSlider.css';
import { applyDynamicGradient } from '../utils/colorExtractor';
import { applyTextAdjustment, TEXT_ADJUSTMENT_PRESETS } from '../utils/textSizeAdjuster';
import { getApiUrl } from '../utils/config';

const API_URL = getApiUrl();

const GameSlider = ({ title, games, collectionType }) => {
    const [failedImages, setFailedImages] = useState(new Set());
    const sliderRef = useRef(null);
    const { isAuth } = useAuth();
    const { t } = useTranslation();

    const handleImageError = useCallback((gameUuid) => {
        setFailedImages(prev => new Set([...prev, gameUuid]));
    }, []);

    const handleImageLoad = useCallback((event, game) => {
        const img = event.target;
        const card = img.closest('.universal-game-card');
        if (card && img) {
            applyDynamicGradient(card, img);

            // Применяем текстовую адаптацию к элементам карточки в слайдере
            const gameName = card.querySelector('.universal-game-name');
            const gameProvider = card.querySelector('.universal-game-provider');

            if (gameName) applyTextAdjustment(gameName, TEXT_ADJUSTMENT_PRESETS.gameName);
            if (gameProvider) applyTextAdjustment(gameProvider, TEXT_ADJUSTMENT_PRESETS.gameProvider);
        }
    }, []);

    const getGameImage = useCallback((game) => {
        if (failedImages.has(game.uuid)) {
            return null; // Будет показано название на черном фоне
        }

        // Бэкенд уже оптимизировал и вернул лучшее изображение в game.image
        if (!game.image || game.image === '') return null;
        
        // Если путь начинается с / - это серверное изображение, добавляем API_URL
        if (game.image.startsWith('/')) {
            return `${API_URL}${game.image}`;
        }
        
        // Иначе это уже полный URL
        return game.image;
    }, [failedImages]);

    // Адаптивная горизонтальная прокрутка
    const scrollLeft = useCallback(() => {
        if (sliderRef.current) {
            const containerWidth = sliderRef.current.clientWidth;
            const screenWidth = window.innerWidth;

            // Определяем количество видимых карточек в зависимости от размера экрана
            let visibleCards;
            if (screenWidth >= 1600) visibleCards = 7;
            else if (screenWidth >= 1400) visibleCards = 6;
            else if (screenWidth >= 1200) visibleCards = 5;
            else if (screenWidth >= 768) visibleCards = 4;
            else visibleCards = 3;

            const scrollAmount = containerWidth * 0.8; // Прокручиваем на 80% контейнера
            const currentScroll = sliderRef.current.scrollLeft;
            const newScroll = Math.max(0, currentScroll - scrollAmount);

            sliderRef.current.scrollTo({
                left: newScroll,
                behavior: 'smooth'
            });
        }
    }, []);

    const scrollRight = useCallback(() => {
        if (sliderRef.current) {
            const containerWidth = sliderRef.current.clientWidth;
            const screenWidth = window.innerWidth;

            // Определяем количество видимых карточек в зависимости от размера экрана
            let visibleCards;
            if (screenWidth >= 1600) visibleCards = 7;
            else if (screenWidth >= 1400) visibleCards = 6;
            else if (screenWidth >= 1200) visibleCards = 5;
            else if (screenWidth >= 768) visibleCards = 4;
            else visibleCards = 3;

            const scrollAmount = containerWidth * 0.8; // Прокручиваем на 80% контейнера
            const currentScroll = sliderRef.current.scrollLeft;
            const maxScroll = sliderRef.current.scrollWidth - sliderRef.current.clientWidth;
            const newScroll = Math.min(maxScroll, currentScroll + scrollAmount);

            sliderRef.current.scrollTo({
                left: newScroll,
                behavior: 'smooth'
            });
        }
    }, []);

    // Проверяем, можно ли прокручивать влево/вправо
    const canScrollLeft = sliderRef.current && sliderRef.current.scrollLeft > 0;
    const canScrollRight = sliderRef.current &&
        sliderRef.current.scrollLeft < (sliderRef.current.scrollWidth - sliderRef.current.clientWidth);

    // Не показываем пустые слайдеры
    if (!games || games.length === 0) {
        return null;
    }

    return (
        <div className="game-slider-container">
            <div className="game-slider-header">
                <h2 className="game-slider-title">
                    {collectionType === 'popular' && (
                        <img src="/assets/banners/icon5.webp" alt="Popular" className="game-slider-icon" />
                    )}
                    {collectionType === 'live' && (
                        <img src="/assets/banners/icon2.webp" alt="Live" className="game-slider-icon" />
                    )}
                    {collectionType === 'fast' && (
                        <img src="/assets/banners/icon3.webp" alt="Fast" className="game-slider-icon" />
                    )}
                    {collectionType === 'new' && (
                        <img src="/assets/banners/icon4.webp" alt="New" className="game-slider-icon" />
                    )}
                    {t(title, title)}
                </h2>
                <div className="game-slider-controls">
                    <button
                        className="slider-control-btn"
                        onClick={scrollLeft}
                        aria-label="Прокрутить влево"
                        disabled={!canScrollLeft}
                        style={{ opacity: canScrollLeft ? 1 : 0.5 }}
                    >
                        <FaChevronLeft />
                    </button>
                    <button
                        className="slider-control-btn"
                        onClick={scrollRight}
                        aria-label="Прокрутить вправо"
                        disabled={!canScrollRight}
                        style={{ opacity: canScrollRight ? 1 : 0.5 }}
                    >
                        <FaChevronRight />
                    </button>
                </div>
            </div>

            <div className="game-slider-wrapper">
                <div className="game-slider" ref={sliderRef}>
                    {games.map((game) => (
                        <Link
                            key={game.uuid}
                            to={`/gis-game/${game.uuid}${!isAuth ? '?demo=true' : ''}${isAuth ? '?' : '&'}name=${encodeURIComponent(game.name)}`}
                            className="universal-game-card game-slider-card"
                        >
                            <div className="universal-game-image-container">
                                {failedImages.has(game.uuid) || !getGameImage(game) ? (
                                    <div className="universal-game-placeholder">
                                        <span className="universal-game-placeholder-title">{game.name}</span>
                                        <span className="universal-game-placeholder-provider">{game.provider}</span>
                                    </div>
                                ) : (
                                    <>
                                        <img
                                            src={getGameImage(game)}
                                            alt={game.name}
                                            className="universal-game-image"
                                            loading="lazy"
                                            onError={() => handleImageError(game.uuid)}
                                            onLoad={(e) => handleImageLoad(e, game)}
                                        />
                                        <div className="universal-game-info">
                                            <div className="universal-game-name">{game.name}</div>
                                            <div className="universal-game-provider">{game.provider}</div>
                                        </div>
                                    </>
                                )}

                                {/* Бейджи */}
                                {game.has_lobby === 1 && (
                                    <div className="universal-game-badge">Лобби</div>
                                )}
                                {game.has_freespins === 1 && (
                                    <div className="universal-game-badge freespins">Фриспины</div>
                                )}
                                {collectionType === 'live' && (
                                    <div className="universal-game-badge live">LIVE</div>
                                )}
                                {collectionType === 'new' && (
                                    <div className="universal-game-badge new">НОВАЯ</div>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default GameSlider; 